import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type { EventJob, CreateEventJobRequest, EventJobConfig } from './live-event-jobs-types';

// ============================================
// Query keys
// ============================================

export const liveEventJobKeys = {
  all: ['livestream', 'event_jobs'] as const,
  config: (serviceId: string) =>
    ['livestream', 'event_job_config', serviceId] as const,
};

// ============================================
// Queries
// ============================================

/**
 * Fetch the full creation config: available presets, output storages, CDNs, and
 * DRM entries. Used to populate the Create Event Job wizard dropdowns.
 * `staleTime` is 5 min — this config changes rarely.
 */
export function useEventJobConfig(serviceId: string) {
  return useQuery<EventJobConfig>({
    queryKey: liveEventJobKeys.config(serviceId),
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/event-job-config`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateEventJob(
  serviceId: string,
  options?: UseMutationOptions<EventJob, Error, CreateEventJobRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEventJobRequest) => {
      const res = await serviceApiV3.post(mediaBase(serviceId, 'streams'), payload);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveEventJobKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
